/* eslint-disable camelcase */
import React, { Fragment } from 'react'
import { PrismicRichText } from '@prismicio/react'
import type { CollectionProducts } from '@lib/types'
import ProductCardSneakPeak from '@components/prismic/ProductCardSneakPeak'
import type { SliceComponentProps } from '@prismicio/react'
import type { Content } from '@prismicio/client'
import ProductGridWithPagination from '@components/prismic/ProductGridWithPagination'
import type { SubsDetailProductsListSliceFeaturedProductPrimary } from '../../../prismicio-types'
import FeaturedProductCard from './FeaturedProductCard'

export type FeaturedProductSubsDetailProductsListProps =
	SliceComponentProps<Content.SubsDetailProductsListSlice> & {
		collectionProducts?: CollectionProducts
		featuredProduct?: any
	}

export default function FeaturedProductSubsDetailProductsList({
	slice,
	collectionProducts = [],
	featuredProduct
}: FeaturedProductSubsDetailProductsListProps) {
	const primary = slice.primary as SubsDetailProductsListSliceFeaturedProductPrimary

	const {
		section_title,
		section_subtitle,
		cta_text,
		cta_link,
		feature_product_description,
		product_list_title,
		product_list_subtitle,
		featured_product_name,
		feature_product_image,
		featured_product_value
	} = primary

	const products = collectionProducts || []

	return (
		<section
			data-slice-type={slice.slice_type}
			data-slice-variation={slice.variation}
			id="sneak-peak"
			className="w-full bg-white py-16 md:pb-20 font-sans scroll-mt-[62px] md:scroll-mt-[76px]"
		>
			<div className="max-w-7xl mx-auto px-4">
				<div className="text-4xl text-center font-serif">
					<PrismicRichText field={section_title} />
				</div>
				{section_subtitle && (
					<div className="text-xl mt-6 text-center max-w-xl mx-auto">
						<PrismicRichText field={section_subtitle} />
					</div>
				)}
				<div className="flex flex-col md:flex-row w-full mt-10 md:mt-20 gap-10">
					{/* --- FEATURED PRODUCT SECTION --- */}

					<div className="flex flex-col flex-1 items-center p-6">
						<div className="md:hidden text-xl font-bold mb-2">Featured Product</div>
						<FeaturedProductCard
							product={{
								image: feature_product_image,
								name: featured_product_name
							}}
							value={featured_product_value}
							className="mb-4"
						/>
						{feature_product_description && (
							<div className="w-full flex flex-col items-left">
								<div className="prose text-base mb-2 text-center md:text-left w-full">
									<PrismicRichText field={feature_product_description} />
								</div>
							</div>
						)}
					</div>

					{/* --- Product list section --- */}
					<div className="flex flex-col md:flex-row flex-1">
						{/* --- Mobile--- */}
						<div className="md:hidden flex flex-col">
							<div>
								<div className="text-xl text-center font-bold">
									<PrismicRichText field={product_list_title} />
								</div>
								{section_subtitle && (
									<div className="text-2xl mt-2 md:whitespace-pre-line text-center">
										<PrismicRichText field={product_list_subtitle} />
									</div>
								)}
							</div>
							{/* Product list (now horizontal scroll) */}
							<div className="flex flex-row gap-4 mt-12 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 -mx-4 px-4">
								{products.map((product) => (
									<div className="w-[38%] flex-shrink-0" key={product.id}>
										<ProductCardSneakPeak key={product.id} product={product} className="" />
									</div>
								))}
							</div>
						</div>
						{/* --- Desktop--- */}
						<div className="hidden md:block h-full w-full">
							<ProductGridWithPagination products={products} className="h-full w-full" />
						</div>
					</div>
				</div>
				{cta_link && cta_text ? (
					<div className="w-full flex justify-center mt-14">
						<a
							className="button button-dark-blue py-5 px-12 max-w-fit-content"
							href={cta_link}
							type="button"
						>
							{cta_text}
						</a>
					</div>
				) : null}
			</div>
		</section>
	)
}
